'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LuChevronRight, LuMenu } from 'react-icons/lu';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@core/ui/primitives/collapsible';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { useSidebarOptional } from '@core/ui/primitives/sidebar';

/**
 * NavMenuSection - Collapsible navigation menu for sidebar
 *
 * Renders page sections, showcase items, and vault links in a collapsible
 * format for the left sidebar when viewport is below wide breakpoint.
 */

interface NavMenuItem {
  label: string;
  href: string;
  isAnchor?: boolean;
}

interface NavMenuGroup {
  title?: string;
  items: NavMenuItem[];
}

interface NavMenuSectionProps {
  /** Groups of nav items to display */
  groups: NavMenuGroup[];
  /** Whether to start expanded (default: true) */
  defaultOpen?: boolean;
}

export function NavMenuSection({ groups, defaultOpen = true }: NavMenuSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const pathname = usePathname() ?? '';
  const sidebar = useSidebarOptional();

  // Close the sidebar overlay when a link is clicked
  const handleLinkClick = () => {
    sidebar?.setOpen(false);
  };

  // Flatten all items to check if any is active
  const allItems = groups.flatMap((g) => g.items);
  const hasActiveItem = allItems.some((item) => {
    if (item.isAnchor) {
      return pathname === '/' || pathname === '/welcome';
    }
    return pathname.startsWith(item.href);
  });

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className='nav-menu-section'>
      <CollapsibleTrigger asChild>
        <button
          type='button'
          className={cn(
            'nav-menu-trigger flex w-full items-center gap-2 px-4 py-2 text-sm font-medium',
            'text-muted-foreground hover:text-foreground',
            'transition-colors duration-200',
            hasActiveItem && 'text-foreground'
          )}>
          <LuMenu className='h-4 w-4' />
          <span className='flex-1 text-left'>Menu</span>
          <LuChevronRight
            className={cn('h-4 w-4 transition-transform duration-200', isOpen && 'rotate-90')}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent
        className={cn(
          'nav-menu-content overflow-hidden',
          'data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down'
        )}>
        <div className='nav-menu-items space-y-1 px-4 pb-3'>
          {groups.map((group, groupIndex) => (
            <div key={groupIndex} className='nav-menu-group'>
              {group.title && (
                <div className='nav-menu-group-title px-2 py-1 text-xs font-medium text-muted-foreground/60 uppercase tracking-wider'>
                  {group.title}
                </div>
              )}
              {group.items.map((item) => {
                // For anchor links, only highlight when on landing page AND hash matches
                // Extract the hash part from href (could be "#anchor" or "/welcome#anchor")
                const anchorHash = item.href.includes('#') ? `#${item.href.split('#')[1]}` : '';
                const isOnLandingPage = pathname === '/' || pathname === '/welcome';
                // Special case: if no hash and pathname is /welcome, treat it as #welcome
                const currentHash =
                  typeof window !== 'undefined'
                    ? window.location.hash || (pathname === '/welcome' ? '#welcome' : '')
                    : '';
                const isActive = item.isAnchor
                  ? isOnLandingPage && currentHash === anchorHash
                  : pathname.startsWith(item.href);

                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={handleLinkClick}
                    className={cn(
                      'nav-menu-item block px-2 py-1.5 text-sm rounded-md',
                      'transition-colors duration-150',
                      isActive
                        ? 'text-emerald-600 dark:text-emerald-400 font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    )}>
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default NavMenuSection;
